import { fetchCheckoutStatics } from '@wix/checkout-components';
import { SentryLogger } from 'services/SentryLogger';

import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Text, Box } from 'wix-style-react';
import { withGtmLogger } from '../../components/HOCs/withGtmLogger';
import { NoWebsiteModal } from '../../components/NoWebsiteModal/advanced/NoWebsiteModal';
import { PageFooter } from '../../components/PageFooter/PageFooter';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { SubscriptionPageController } from './SubscriptionPage.controller';
import { SubscriptionPageProps } from './SubscriptionPageProps';
import { Cycles } from './Cycles/Cycles';
import { CyclesHeading } from './Cycles/CyclesHeading';
import s from './SubscriptionPage.scss';
import { SubscriptionSelectButton } from './SubscriptionSelectButton';
import { PendingCycleChangeNotification } from './PendingCycleChangeNotification/PendingCycleChangeNotification';

const SubscriptionPageComp: React.FC<SubscriptionPageProps> = (props) => {
  React.useEffect(() => {
    try {
      fetchCheckoutStatics();
    } catch (e) {
      SentryLogger.logError(e);
    }
  }, []);

  const controller = React.useMemo(
    () => new SubscriptionPageController(props),
    [],
  );
  const {
    getFooterKey,
    onCycleSelect,
    serviceHasPendingChanges,
    servicePendingChangesInfo,
  } = controller;
  const {
    setNoWebsiteModal,
    isNoWebsiteModalOpened,
    isSaleProduct,
    isInSale,
    isEditorXSpecialDiscountEnabled,
    isVoucherDisclaimerHidden,
  } = props.rootStore.appStore;
  const { t } = useTranslation();
  const { theme: themeName } = usePageModeContext();
  const translationTags = {
    b: <b />,
    voucher: (
      <span
        data-hook='voucher-disclaimer'
        className={isVoucherDisclaimerHidden ? s.hiddenLabel : s.visibleLabel}
      />
    ),
  };
  return (
    <div
      className={classNames(
        s.subscriptionContainer,
        {
          [s.isInSale]: isSaleProduct,
        },
        themeName,
      )}
      data-hook='subscription-container'
    >
      <div className={classNames(s.cycleSelector, themeName)}>
        <div className={classNames(s.subscription, themeName)}>
          <div className={s.cyclesAndHeader}>
            <CyclesHeading />
            {serviceHasPendingChanges && (
              <Box margin='SP5 SP4'>
                <PendingCycleChangeNotification
                  servicePendingChangesInfo={servicePendingChangesInfo!}
                />
              </Box>
            )}
            <Cycles />
          </div>
          <SubscriptionSelectButton
            onCycleSelect={onCycleSelect}
            className={classNames({ 'sale-button': isInSale }, themeName)}
          />
        </div>
      </div>
      <NoWebsiteModal
        onClose={() => setNoWebsiteModal(false)}
        isOpen={isNoWebsiteModalOpened}
      />
      <PageFooter>
        {isEditorXSpecialDiscountEnabled && (
          <div>
            <Text
              weight='thin'
              size='tiny'
              dataHook='special-discount-disclaimer'
              secondary
            >
              {t('special_discount_disclaimer')}
            </Text>
          </div>
        )}
        <Text weight='thin' size='tiny' dataHook='disclamer' secondary>
          <Trans i18nKey={getFooterKey()} components={translationTags} />
        </Text>
      </PageFooter>
    </div>
  );
};

export const SubscriptionPage = withInjectedRootStore(
  withExperiments(withGtmLogger(observer(SubscriptionPageComp))),
);
